import { createContext, FunctionComponent, ReactNode, useContext } from 'react';
import { Link as RouterLink, useInRouterContext } from 'react-router-dom';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import {
  Box,
  Divider,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { slugifyHeading } from './docsHeadings';
import { DocsImage } from './DocsImage';
// Highlight.js theme for fenced code blocks. rehype-highlight adds the `.hljs`
// class names this stylesheet targets; it is third-party plugin CSS, not custom
// app CSS, so the "MUI-only / no custom CSS" rule still holds.
import 'highlight.js/styles/github.css';

// Renders a Markdown string with react-markdown. GitHub Flavored Markdown (tables,
// task lists, strikethrough, autolinks) comes from remark-gfm; fenced code blocks
// are syntax-highlighted by rehype-highlight. Every element is mapped to a MUI
// primitive so the docs content inherits the app theme without any custom CSS.
// Raw HTML in the Markdown is intentionally not parsed (no rehype-raw) so authored
// content cannot inject markup.

// Flattens a React node tree to plain text — used to derive heading anchor ids
// that match the table of contents.
function nodeToText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join('');
  if (typeof node === 'object' && 'props' in node)
    return nodeToText((node as { props: { children?: ReactNode } }).props.children);
  return '';
}

interface ChildrenProps {
  children?: ReactNode;
}

// Builds a heading component that renders the given MUI Typography variant and
// attaches an anchor id so the TOC can scroll to it.
function heading(variant: 'h4' | 'h5' | 'h6', mt: number) {
  const Heading: FunctionComponent<ChildrenProps> = ({ children }) => (
    <Typography
      id={slugifyHeading(nodeToText(children))}
      variant={variant}
      sx={{ mt, mb: 1.5, fontWeight: 600, scrollMarginTop: '88px' }}>
      {children}
    </Typography>
  );
  return Heading;
}

// Accepts only explicit safe schemes (http, https, mailto), same-site relative
// paths (single leading slash), and fragment-only links (#). Protocol-relative
// URLs (//example.com) are intentionally excluded — they resolve as external
// URLs in the browser and bypass the isExternal target/rel guard below.
const SAFE_URL_PATTERN = /^(https?:|mailto:|\/(?!\/)|#)/i;

// Renders a same-site path link. Falls back to a plain MUI Link (standard <a>)
// when rendered outside a Router context so DocsMarkdown stays safe in tests,
// Storybook, or any other non-routed environment.
const DocsInternalLink: FunctionComponent<{ href: string; children?: ReactNode }> = ({ href, children }) => {
  const inRouter = useInRouterContext();
  if (inRouter) {
    return (
      <Link
        component={RouterLink}
        to={href}>
        {children}
      </Link>
    );
  }
  return <Link href={href}>{children}</Link>;
};

function sanitizeHref(href: string | undefined): string | undefined {
  if (!href) return undefined;
  return SAFE_URL_PATTERN.test(href) ? href : undefined;
}

// A docs role preview loads the page's images as that role, as it loaded the page.
const SimulateRoleContext = createContext<string | undefined>(undefined);

const DocsMarkdownImage: FunctionComponent<{ src?: string; alt?: string; title?: string }> = (props) => {
  const simulateRole = useContext(SimulateRoleContext);
  return (
    <DocsImage
      {...props}
      simulateRole={simulateRole}
    />
  );
};

const inlineCodeSx = {
  fontFamily: 'monospace',
  bgcolor: 'grey.100',
  px: 0.75,
  py: 0.25,
  borderRadius: 1,
  fontSize: '0.875em'
} as const;

// Maps every Markdown/HTML element react-markdown emits to a MUI primitive.
const components: Components = {
  h1: heading('h4', 0),
  h2: heading('h5', 4),
  h3: heading('h6', 3),
  h4: heading('h6', 2),
  h5: heading('h6', 2),
  h6: heading('h6', 2),
  p: ({ children }) => (
    <Typography
      component="div"
      variant="body1"
      sx={{ mb: 2, lineHeight: 1.7 }}>
      {children}
    </Typography>
  ),
  a: ({ href, children }) => {
    const safeHref = sanitizeHref(href);
    const isExternal = safeHref?.toLowerCase().startsWith('http');
    // Same-site paths (single leading slash) route through React Router so docs
    // navigation stays client-side instead of triggering a full page reload.
    // External (http/https) and fragment-only (#) links keep plain anchor behavior.
    const isInternalPath = safeHref?.startsWith('/');
    if (isInternalPath) {
      return <DocsInternalLink href={safeHref as string}>{children}</DocsInternalLink>;
    }
    return (
      <Link
        href={safeHref}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}>
        {children}
      </Link>
    );
  },
  ul: ({ className, children }) => {
    const isTaskList = className?.includes('contains-task-list');
    return (
      <Box
        component="ul"
        sx={{ pl: isTaskList ? 0 : 3, mb: 2, listStyle: isTaskList ? 'none' : undefined }}>
        {children}
      </Box>
    );
  },
  ol: ({ children }) => (
    <Box
      component="ol"
      sx={{ pl: 3, mb: 2 }}>
      {children}
    </Box>
  ),
  li: ({ className, children }) => {
    const isTask = className?.includes('task-list-item');
    return (
      <Typography
        component="li"
        variant="body1"
        sx={{ mb: 0.5, lineHeight: 1.7, listStyle: isTask ? 'none' : undefined }}>
        {children}
      </Typography>
    );
  },
  del: ({ children }) => <Box component="del">{children}</Box>,
  code: ({ className, children }) => {
    const isBlock = /language-/.test(className || '') || nodeToText(children).includes('\n');
    if (isBlock) {
      // Block code: keep the className so rehype-highlight's spans + theme apply.
      return <code className={className}>{children}</code>;
    }
    return (
      <Box
        component="code"
        sx={inlineCodeSx}>
        {children}
      </Box>
    );
  },
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        bgcolor: 'grey.100',
        p: 2,
        borderRadius: 1,
        mb: 2,
        overflowX: 'auto',
        fontSize: '0.875em',
        '& code': { fontFamily: 'monospace' }
      }}>
      {children}
    </Box>
  ),
  blockquote: ({ children }) => (
    <Box
      component="blockquote"
      sx={{
        borderLeft: 4,
        borderColor: 'primary.main',
        pl: 2,
        ml: 0,
        my: 2,
        color: 'text.secondary'
      }}>
      {children}
    </Box>
  ),
  hr: () => <Divider sx={{ my: 3 }} />,
  img: ({ src, alt, title }) => (
    <DocsMarkdownImage
      src={typeof src === 'string' ? src : undefined}
      alt={alt}
      title={title}
    />
  ),
  table: ({ children }) => (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ mb: 2 }}>
      <Table size="small">{children}</Table>
    </TableContainer>
  ),
  thead: ({ children }) => <TableHead>{children}</TableHead>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <TableRow>{children}</TableRow>,
  th: ({ children, style }) => (
    <TableCell
      component="th"
      sx={{
        fontWeight: 600,
        textAlign: style?.textAlign,
        borderRight: '1px solid',
        borderRightColor: 'divider',
        '&:last-child': { borderRight: 'none' }
      }}>
      {children}
    </TableCell>
  ),
  td: ({ children, style }) => (
    <TableCell
      sx={{
        textAlign: style?.textAlign,
        borderRight: '1px solid',
        borderRightColor: 'divider',
        '&:last-child': { borderRight: 'none' }
      }}>
      {children}
    </TableCell>
  )
};

interface DocsMarkdownProps {
  markdown: string;
  simulateRole?: string;
}

export const DocsMarkdown: FunctionComponent<DocsMarkdownProps> = ({ markdown, simulateRole }) => (
  <SimulateRoleContext.Provider value={simulateRole}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeHighlight]}
      components={components}>
      {markdown}
    </ReactMarkdown>
  </SimulateRoleContext.Provider>
);
